import React, { useState, useEffect } from "react"
import { X, Search, Zap, Code, Share2, GitBranch } from "lucide-react"
import "./OutlinePanel.css"

const OutlinePanel = ({ nodes, edges, onClose, onNodeSelect }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [orderedNodes, setOrderedNodes] = useState([])
  const [selectedNodeId, setSelectedNodeId] = useState(null)

  // Function to get node type icon
  const getNodeIcon = (nodeType) => {
    switch (nodeType) {
      case "triggerNode":
        return <Zap size={16} className="node-icon trigger-icon" />
      case "actionNode":
      case "codeNode":
        return <Code size={16} className="node-icon code-icon" />
      default:
        // Default icon based on node type
        const iconMap = {
          dataNode: <div className="node-icon data-icon">D</div>,
          aiNode: <div className="node-icon ai-icon">AI</div>,
          responseNode: <div className="node-icon response-icon">R</div>,
          workflowNode: <Share2 size={16} className="node-icon workflow-icon" />,
          branchNode: <GitBranch size={16} className="node-icon branch-icon" />,
        }
        return iconMap[nodeType] || <div className="node-icon">•</div>
    }
  }

  // Order nodes based on connections
  useEffect(() => {
    const orderNodesByConnection = () => {
      // Start with trigger nodes (usually the entry points)
      const triggerNodes = nodes.filter((node) => node.type === "triggerNode")
      if (triggerNodes.length === 0) return nodes

      const ordered = []
      const visited = new Set()

      // Helper function for depth-first traversal
      const traverseNodes = (nodeId) => {
        if (visited.has(nodeId)) return

        const node = nodes.find((n) => n.id === nodeId)
        if (!node) return

        visited.add(nodeId)
        ordered.push(node)

        // Find all edges where this node is the source
        const outgoingEdges = edges.filter((edge) => edge.source === nodeId)

        // Follow each edge to the target node
        outgoingEdges.forEach((edge) => {
          traverseNodes(edge.target)
        })
      }

      // Start traversal from each trigger node
      triggerNodes.forEach((node) => {
        traverseNodes(node.id)
      })

      // Add any remaining nodes that weren't connected
      nodes.forEach((node) => {
        if (!visited.has(node.id)) {
          ordered.push(node)
        }
      })

      return ordered
    }

    setOrderedNodes(orderNodesByConnection())
  }, [nodes, edges])

  // Filter nodes based on search term
  const filteredNodes = orderedNodes.filter((node) => node.data.label.toLowerCase().includes(searchTerm.toLowerCase()))

  const handleNodeClick = (nodeId) => {
    setSelectedNodeId(nodeId)
    if (onNodeSelect) {
      onNodeSelect(nodeId)
    }
  }

  return (
    <div className="outline-panel">
      <div className="outline-header">
        <h2>Outline</h2>
        <div className="outline-actions">
          <button className="outline-filter-button">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M14 2H2L7 8.5V13L9 14V8.5L14 2Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button className="outline-close-button" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="outline-search">
        <Search size={14} className="search-icon" />
        <input type="text" placeholder="Filter" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </div>

      <div className="outline-nodes">
        {filteredNodes.map((node) => (
          <div
            key={node.id}
            className={`outline-node ${selectedNodeId === node.id ? "selected" : ""}`}
            onClick={() => handleNodeClick(node.id)}
          >
            <div className="node-icon-container">{getNodeIcon(node.type)}</div>
            <span className="node-label">{node.data.label}</span>
          </div>
        ))}

        {filteredNodes.length === 0 && <div className="no-nodes">No nodes match your filter</div>}
      </div>
    </div>
  )
}

export default OutlinePanel
